const DB_NAME = 'smfd-sac-protocols-v1';
const DB_VERSION = 1;
const META_STORE = 'meta';
const PDF_STORE = 'pdfs';
const MANIFEST_KEY = 'manifest';

function openDb() {
  if (typeof window === 'undefined' || !window.indexedDB) {
    return Promise.reject(new Error('Offline storage is not available on this device.'));
  }

  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(META_STORE)) db.createObjectStore(META_STORE);
      if (!db.objectStoreNames.contains(PDF_STORE)) db.createObjectStore(PDF_STORE, { keyPath: 'url' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Unable to open offline protocol storage.'));
  });
}

function transact(storeName, mode, work) {
  return openDb().then(db => new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    let result;
    try {
      result = work(store);
    } catch (error) {
      db.close();
      reject(error);
      return;
    }
    transaction.oncomplete = () => { db.close(); resolve(result); };
    transaction.onerror = () => { db.close(); reject(transaction.error || new Error('Offline protocol storage failed.')); };
    transaction.onabort = () => { db.close(); reject(transaction.error || new Error('Offline protocol storage was interrupted.')); };
  }));
}

function requestResult(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Offline protocol storage request failed.'));
  });
}

export async function readProtocolManifest() {
  const db = await openDb();
  try {
    const transaction = db.transaction(META_STORE, 'readonly');
    return await requestResult(transaction.objectStore(META_STORE).get(MANIFEST_KEY));
  } finally {
    db.close();
  }
}

export function writeProtocolManifest(manifest) {
  return transact(META_STORE, 'readwrite', store => {
    store.put(manifest, MANIFEST_KEY);
  });
}

export async function readProtocolPdf(url) {
  const db = await openDb();
  try {
    const transaction = db.transaction(PDF_STORE, 'readonly');
    return await requestResult(transaction.objectStore(PDF_STORE).get(String(url || '')));
  } finally {
    db.close();
  }
}

export function writeProtocolPdf(protocol, blob) {
  const url = String(protocol?.url || '').trim();
  if (!url || !(blob instanceof Blob)) return Promise.reject(new Error('A protocol URL and PDF are required.'));
  return transact(PDF_STORE, 'readwrite', store => {
    store.put({
      url,
      id: String(protocol?.id || ''),
      number: String(protocol?.number || ''),
      revision: String(protocol?.revision || ''),
      title: String(protocol?.title || ''),
      category: String(protocol?.category || ''),
      savedAt: new Date().toISOString(),
      blob,
    });
  });
}

export async function listCachedProtocolUrls() {
  const db = await openDb();
  try {
    const transaction = db.transaction(PDF_STORE, 'readonly');
    const keys = await requestResult(transaction.objectStore(PDF_STORE).getAllKeys());
    return keys.map(String);
  } finally {
    db.close();
  }
}

export function pruneProtocolPdfs(validUrls) {
  const keep = new Set((validUrls || []).map(String));
  return transact(PDF_STORE, 'readwrite', store => {
    const cursor = store.openKeyCursor();
    cursor.onsuccess = () => {
      const current = cursor.result;
      if (!current) return;
      if (!keep.has(String(current.key))) store.delete(current.key);
      current.continue();
    };
  });
}

export function protocolStorageSupported() {
  return typeof window !== 'undefined' && Boolean(window.indexedDB);
}
